import React, { useState, useEffect, useRef } from 'react';
import {
  X, UserPlus, Mail, Copy, ExternalLink, Loader, CheckCircle2, AlertCircle, ArrowLeft,
  CheckSquare, Square, Search
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member', description: 'Can view the dashboard, accounts and billing' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to the dashboard' }
];

export default function InviteMemberModal({ open, onClose, organizationName, onInvited }) {
  const { theme } = useTheme();
  const { showToast } = useToast();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [success, setSuccess] = useState(null); // { email, acceptUrl, emailSent }

  // Account scope
  const [accountScope, setAccountScope] = useState('all'); // 'all' | 'restricted'
  const [orgAccounts, setOrgAccounts] = useState([]); // [{ customerId, accountName }]
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [accountSearch, setAccountSearch] = useState('');
  const accountsRequestedRef = useRef(false);

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('member');
      setMessage('');
      setErrorMsg(null);
      setSuccess(null);
      setSubmitting(false);
      setAccountScope('all');
      setSelectedAccounts(new Set());
      setAccountSearch('');
      setOrgAccounts([]);
      setAccountsLoading(false);
      accountsRequestedRef.current = false;
    }
  }, [open]);

  // Load the org's accounts the first time the owner switches to "specific".
  // Guarded by a ref so toggling `accountsLoading` doesn't cancel the request.
  useEffect(() => {
    if (!open || accountScope !== 'restricted' || accountsRequestedRef.current) return;
    accountsRequestedRef.current = true;
    setAccountsLoading(true);
    (async () => {
      try {
        const res = await apiCall('/api/partner/organization/accounts');
        const data = await res.json();
        if (data.success) {
          setOrgAccounts(data.availableAccounts || []);
        } else {
          showToast(data.error || 'Could not load accounts', 'error');
        }
      } catch {
        showToast('Could not load accounts', 'error');
      } finally {
        setAccountsLoading(false);
      }
    })();
  }, [open, accountScope, showToast]);

  if (!open) return null;

  const toggleAccount = (customerId) => {
    setSelectedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  };

  const filteredAccounts = orgAccounts.filter(a => {
    if (!accountSearch.trim()) return true;
    const q = accountSearch.trim().toLowerCase();
    return (a.accountName || '').toLowerCase().includes(q)
      || String(a.customerId).includes(q.replace(/\D/g, ''));
  });

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setErrorMsg('Please enter an email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg('That doesn\'t look like a valid email address.');
      return;
    }
    if (accountScope === 'restricted' && selectedAccounts.size === 0) {
      setErrorMsg('Select at least one account, or choose "All accounts".');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);
    try {
      const response = await apiCall('/api/partner/organization/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          role,
          message: message.trim() || undefined,
          accountScope,
          accountCustomerIds: accountScope === 'restricted' ? Array.from(selectedAccounts) : []
        })
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Could not send invite');
      }
      setSuccess({
        email: data.invite?.email || trimmed,
        acceptUrl: data.acceptUrl,
        emailSent: data.emailSent !== false
      });
      onInvited?.();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!success?.acceptUrl) return;
    try {
      await navigator.clipboard.writeText(success.acceptUrl);
      showToast('Invite link copied', 'success');
    } catch {
      showToast('Could not copy link', 'error');
    }
  };

  const handleSendAnother = () => {
    setEmail('');
    setRole('member');
    setMessage('');
    setSuccess(null);
    setErrorMsg(null);
    setAccountScope('all');
    setSelectedAccounts(new Set());
    setAccountSearch('');
  };

  const inputBase = {
    width: '100%',
    background: theme.colors.bg.tertiary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 8,
    padding: '10px 14px',
    color: theme.colors.text.primary,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box'
  };

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 16
  };

  const modalStyle = {
    background: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 14,
    width: '100%',
    maxWidth: 520,
    boxShadow: theme.mode === 'light'
      ? '0 24px 48px rgba(0,0,0,0.18)'
      : '0 24px 48px rgba(0,0,0,0.5)',
    overflow: 'hidden'
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '18px 22px',
          borderBottom: `1px solid ${theme.colors.border.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'rgba(139, 92, 246, 0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <UserPlus size={17} color="#8b5cf6" />
            </div>
            <div>
              <div style={{ color: theme.colors.text.primary, fontSize: 16, fontWeight: 700 }}>
                {success ? 'Invitation sent' : 'Invite a new team member'}
              </div>
              {organizationName && (
                <div style={{ color: theme.colors.text.tertiary, fontSize: 12 }}>
                  for {organizationName}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              color: theme.colors.text.muted, cursor: 'pointer', padding: 6
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        {!success ? (
          <form onSubmit={handleSubmit} style={{ padding: '20px 22px' }}>
            <p style={{
              margin: '0 0 18px',
              color: theme.colors.text.secondary,
              fontSize: 13,
              lineHeight: 1.55
            }}>
              We'll email an invite link to your colleague. Don't have a Bidoryx account yet?
              No problem — they can create one through the link and will be added to your team
              automatically.
            </p>

            <label style={{
              display: 'block', color: theme.colors.text.secondary,
              fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4
            }}>
              Email address
            </label>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrorMsg(null); }}
              placeholder="colleague@company.com"
              style={inputBase}
              disabled={submitting}
            />

            <label style={{
              display: 'block', color: theme.colors.text.secondary,
              fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 6,
              textTransform: 'uppercase', letterSpacing: 0.4
            }}>
              Role
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ROLE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 12px',
                    border: `1px solid ${role === opt.value ? '#8b5cf6' : theme.colors.border.primary}`,
                    background: role === opt.value
                      ? 'rgba(139, 92, 246, 0.08)'
                      : theme.colors.bg.tertiary,
                    borderRadius: 8, cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  <input
                    type="radio"
                    name="role"
                    value={opt.value}
                    checked={role === opt.value}
                    onChange={() => setRole(opt.value)}
                    style={{ marginTop: 3 }}
                    disabled={submitting}
                  />
                  <div>
                    <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600 }}>
                      {opt.label}
                    </div>
                    <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginTop: 2 }}>
                      {opt.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <label style={{
              display: 'block', color: theme.colors.text.secondary,
              fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 6,
              textTransform: 'uppercase', letterSpacing: 0.4
            }}>
              Account access
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { value: 'all', label: 'All accounts', description: 'Can see every client account in your organization' },
                { value: 'restricted', label: 'Specific accounts', description: 'Can only see the accounts you select below' }
              ].map(opt => (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 12px',
                    border: `1px solid ${accountScope === opt.value ? '#8b5cf6' : theme.colors.border.primary}`,
                    background: accountScope === opt.value
                      ? 'rgba(139, 92, 246, 0.08)'
                      : theme.colors.bg.tertiary,
                    borderRadius: 8, cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  <input
                    type="radio"
                    name="accountScope"
                    value={opt.value}
                    checked={accountScope === opt.value}
                    onChange={() => { setAccountScope(opt.value); setErrorMsg(null); }}
                    style={{ marginTop: 3 }}
                    disabled={submitting}
                  />
                  <div>
                    <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600 }}>
                      {opt.label}
                    </div>
                    <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginTop: 2 }}>
                      {opt.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {accountScope === 'restricted' && (
              <div style={{
                marginTop: 10,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: 8,
                background: theme.colors.bg.tertiary,
                overflow: 'hidden'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px',
                  borderBottom: `1px solid ${theme.colors.border.secondary}`
                }}>
                  <Search size={14} color={theme.colors.text.muted} />
                  <input
                    type="text"
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    placeholder="Search accounts…"
                    style={{
                      flex: 1, background: 'transparent', border: 'none', outline: 'none',
                      color: theme.colors.text.primary, fontSize: 13
                    }}
                    disabled={submitting}
                  />
                  <span style={{ color: theme.colors.text.tertiary, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {selectedAccounts.size} selected
                  </span>
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {accountsLoading ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '24px 12px', color: theme.colors.text.tertiary, fontSize: 13
                    }}>
                      <Loader size={14} className="animate-spin" /> Loading accounts…
                    </div>
                  ) : filteredAccounts.length === 0 ? (
                    <div style={{
                      padding: '20px 12px', textAlign: 'center',
                      color: theme.colors.text.tertiary, fontSize: 13
                    }}>
                      {orgAccounts.length === 0
                        ? 'No client accounts in your organization yet.'
                        : 'No accounts match your search.'}
                    </div>
                  ) : (
                    filteredAccounts.map(acc => {
                      const checked = selectedAccounts.has(acc.customerId);
                      return (
                        <button
                          type="button"
                          key={acc.customerId}
                          onClick={() => toggleAccount(acc.customerId)}
                          disabled={submitting}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                            padding: '9px 12px', background: 'transparent', border: 'none',
                            borderBottom: `1px solid ${theme.colors.border.secondary}`,
                            cursor: 'pointer', textAlign: 'left'
                          }}
                        >
                          {checked
                            ? <CheckSquare size={16} color="#8b5cf6" style={{ flexShrink: 0 }} />
                            : <Square size={16} color={theme.colors.text.muted} style={{ flexShrink: 0 }} />}
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              color: theme.colors.text.primary, fontSize: 13, fontWeight: 500,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                            }}>
                              {acc.accountName}
                            </div>
                            <div style={{ color: theme.colors.text.tertiary, fontSize: 11 }}>
                              {acc.customerId}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            <label style={{
              display: 'block', color: theme.colors.text.secondary,
              fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 6,
              textTransform: 'uppercase', letterSpacing: 0.4
            }}>
              Personal message (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hey! Giving you access to our Bidoryx Partner Dashboard…"
              rows={3}
              maxLength={500}
              style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
              disabled={submitting}
            />

            {errorMsg && (
              <div style={{
                marginTop: 14,
                padding: '10px 12px',
                background: 'rgba(239, 68, 68, 0.10)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 8,
                color: '#ef4444',
                fontSize: 13,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8
              }}>
                <AlertCircle size={15} style={{ marginTop: 1, flexShrink: 0 }} />
                <span>{errorMsg}</span>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              marginTop: 22
            }}>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: 8,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: theme.colors.text.secondary,
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !email.trim()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#8b5cf6',
                  border: '1px solid #8b5cf6',
                  borderRadius: 8,
                  padding: '10px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#fff',
                  cursor: (submitting || !email.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (submitting || !email.trim()) ? 0.6 : 1
                }}
              >
                {submitting ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
                {submitting ? 'Sending…' : 'Send invitation'}
              </button>
            </div>
          </form>
        ) : (
          <div style={{ padding: '24px 22px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              marginBottom: 16
            }}>
              <div style={{
                width: 42, height: 42, borderRadius: '50%',
                background: 'rgba(16, 185, 129, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <CheckCircle2 size={22} color="#10b981" />
              </div>
              <div>
                <div style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 600 }}>
                  {success.emailSent ? 'Invitation sent!' : 'Invitation created'}
                </div>
                <div style={{ color: theme.colors.text.tertiary, fontSize: 13, marginTop: 2 }}>
                  {success.emailSent
                    ? `We've emailed an invite link to ${success.email}.`
                    : `We couldn't send the email, but you can copy the link below and share it yourself.`}
                </div>
              </div>
            </div>

            <div style={{
              padding: '12px 14px',
              background: theme.colors.bg.tertiary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: 8,
              marginBottom: 14
            }}>
              <div style={{ color: theme.colors.text.tertiary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>
                Invite link (valid 14 days)
              </div>
              <div style={{
                display: 'flex', gap: 8, alignItems: 'center'
              }}>
                <code style={{
                  flex: 1,
                  fontSize: 12,
                  color: theme.colors.text.primary,
                  background: 'transparent',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {success.acceptUrl}
                </code>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'rgba(139, 92, 246, 0.15)',
                    color: '#8b5cf6',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: 6, padding: '6px 10px',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer'
                  }}
                  title="Copy link"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>

            <div style={{
              padding: '12px 14px',
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              borderRadius: 8,
              fontSize: 12,
              color: theme.colors.text.secondary,
              lineHeight: 1.5,
              marginBottom: 18
            }}>
              <strong style={{ color: theme.colors.text.primary }}>How it works:</strong>
              {' '}your colleague clicks the link. If they don't have an account yet they'll be asked
              to sign up with this email address. After that, they'll automatically appear in your team.
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10
            }}>
              <button
                type="button"
                onClick={handleSendAnother}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'transparent',
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: 8, padding: '10px 14px',
                  fontSize: 13, fontWeight: 600,
                  color: theme.colors.text.secondary, cursor: 'pointer'
                }}
              >
                <ArrowLeft size={13} /> Invite someone else
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#8b5cf6',
                  border: '1px solid #8b5cf6',
                  borderRadius: 8, padding: '10px 18px',
                  fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer'
                }}
              >
                Done <ExternalLink size={13} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
